import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BookOpen,
  CalendarClock,
  Car,
  Check,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  GraduationCap,
  History,
  Loader2,
  Search,
  UserMinus,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import { instructorApi } from '../api/instructor';
import { useUiStore } from '../store/ui';
import PhoneLink from '../components/PhoneLink';
import { fmtDate, initials } from '../lib/dates';
import { StudentPhoto } from '../components/Photos';
import { CandidateSearchResult, Student } from '../types';
import { Badge, Button, Card, ConfirmDialog, Drawer, ProgressBar, Segmented, cx, useToast } from '../components/charte';
import { Sheet } from '../components/shell/Sheet';

// Mes élèves (refonte du 14/10/2026, tablette d'abord) : cartes avec la progression ; un toucher ouvre le panneau
// de l'élève (infos et toutes les actions : livret et résumé de la séance, historique, évaluation, prévision d'examen).
// « Ajouter un élève » passe derrière un bouton ; une recherche filtre la liste.
// J37 (28/10/2026) : la liste se remplit seule des élèves avec qui le moniteur a fait au moins une heure ;
// « Retirer de mes élèves » n'est proposé que s'il fait sortir l'élève de la liste.

type Filter = 'tous' | 'prets' | 'examen';

const gearbox = (s: { vehicleType: string }) => (s.vehicleType === 'automatic' ? 'Boîte automatique' : 'Boîte manuelle');
const percent = (s: Student) =>
  s.drivingHoursTarget > 0 ? Math.min(100, Math.round((s.drivingHoursDone / s.drivingHoursTarget) * 100)) : 0;
const normalize = (v: string) => v.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

function StudentAvatar({ student, size = 'md' }: { student: { id: string; firstName: string; lastName: string }; size?: 'md' | 'lg' }) {
  return (
    <span
      className={cx(
        'relative grid shrink-0 place-items-center overflow-hidden rounded-full bg-avatar font-semibold text-primary-700',
        size === 'lg' ? 'h-14 w-14 text-base' : 'h-12 w-12 text-sm',
      )}
    >
      {initials(student.firstName, student.lastName)}
      <StudentPhoto candidateId={student.id} />
    </span>
  );
}

function StatusBadges({ student }: { student: Student }) {
  return (
    <>
      {student.readyForExam && (
        <Badge tone="ok">
          <CheckCircle2 size={12} aria-hidden />
          Prêt(e) pour l'examen
        </Badge>
      )}
      {student.examDate && (
        <Badge tone="todo">
          <GraduationCap size={12} aria-hidden />
          Examen le {fmtDate(student.examDate)}
        </Badge>
      )}
    </>
  );
}

function StudentCard({ student, onOpen }: { student: Student; onOpen: () => void }) {
  const name = `${student.firstName} ${student.lastName}`;
  return (
    <li className="relative">
      <button
        type="button"
        onClick={onOpen}
        className="flex h-full w-full flex-col gap-3 rounded-card bg-white p-5 text-left shadow-card transition-all hover:-translate-y-0.5 hover:shadow-[0_0_0_1px_#C7D2FE,0_10px_30px_-2px_rgba(0,0,0,.06)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
      >
        <span className="flex items-start gap-3 pr-12">
          <StudentAvatar student={student} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[15px] font-semibold text-heading">{name}</span>
            <span className="block text-[13px] text-secondary">{gearbox(student)}</span>
          </span>
        </span>
        {(student.readyForExam || student.examDate) && (
          <span className="flex flex-wrap gap-1.5">
            <StatusBadges student={student} />
          </span>
        )}
        <span className="block">
          <span className="mb-1.5 flex items-center justify-between text-[13px]">
            <span className="text-secondary">Heures de conduite</span>
            <span className="font-semibold tabular-nums text-heading">
              {student.drivingHoursDone}/{student.drivingHoursTarget}h
            </span>
          </span>
          <span className="block h-2 overflow-hidden rounded-full bg-surface-2">
            <span className="block h-full rounded-full bg-btn-primary" style={{ width: `${percent(student)}%` }} />
          </span>
        </span>
        <span className="mt-auto flex items-center gap-2 text-[13px] text-secondary">
          <Car size={14} className="text-muted" aria-hidden />
          {student.nextLessonAt ? `Prochaine leçon : ${fmtDate(student.nextLessonAt)}` : 'Aucune leçon planifiée'}
          <ChevronRight size={16} className="ml-auto text-muted" aria-hidden />
        </span>
      </button>
      {/* L'appel reste à portée sans ouvrir le panneau. */}
      <span className="absolute right-4 top-4">
        <PhoneLink phone={student.phoneNumber} name={name} compact />
      </span>
    </li>
  );
}

function ActionRow({ icon: Icon, label, hint, onClick }: { icon: LucideIcon; label: string; hint?: string; onClick: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex min-h-[56px] w-full items-center gap-3 rounded-row px-3 py-2.5 text-left transition-colors hover:bg-surface-light"
      >
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-surface-indigo text-primary">
          <Icon size={18} aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-heading">{label}</span>
          {hint && <span className="block text-xs text-secondary">{hint}</span>}
        </span>
        <ChevronRight size={16} className="text-muted" aria-hidden />
      </button>
    </li>
  );
}

function StudentPanel({ student, onClose }: { student: Student; onClose: () => void }) {
  const ui = useUiStore();
  const queryClient = useQueryClient();
  const toast = useToast();
  const name = `${student.firstName} ${student.lastName}`;
  const [confirmRemove, setConfirmRemove] = useState(false);
  const remove = useMutation({
    mutationFn: () => instructorApi.removeStudent(student.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['students'] });
      void queryClient.invalidateQueries({ queryKey: ['candidate-search'] });
      toast(`${name} a été retiré(e) de votre liste.`);
      onClose();
    },
  });
  // Une page s'ouvre par-dessus la rubrique : le panneau se referme.
  const go = (action: () => void) => () => {
    onClose();
    action();
  };
  return (
    <Drawer
      open
      onClose={onClose}
      width={480}
      leading={<StudentAvatar student={student} size="lg" />}
      title={name}
      subtitle={gearbox(student)}
    >
      <div className="space-y-5">
        {(student.readyForExam || student.examDate || student.phoneNumber) && (
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadges student={student} />
            <PhoneLink phone={student.phoneNumber} name={name} />
          </div>
        )}

        <Card className="space-y-3 bg-surface-light p-4 shadow-none">
          <div className="flex items-baseline justify-between text-sm">
            <span className="text-secondary">Heures de conduite</span>
            <span className="font-semibold tabular-nums text-heading">
              {student.drivingHoursDone}/{student.drivingHoursTarget}h
            </span>
          </div>
          <ProgressBar value={percent(student)} label="Heures de conduite" />
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 pt-1 text-sm">
            <dt className="text-secondary">Prochaine leçon</dt>
            <dd className="text-right font-medium text-heading">{student.nextLessonAt ? fmtDate(student.nextLessonAt) : 'Aucune'}</dd>
            <dt className="text-secondary">Examen</dt>
            <dd className="text-right font-medium text-heading">{student.examDate ? fmtDate(student.examDate) : 'à planifier'}</dd>
            <dt className="text-secondary">Dernier résumé</dt>
            <dd className="text-right font-medium text-heading">{student.lastBilanAt ? fmtDate(student.lastBilanAt) : 'Aucun'}</dd>
            {student.hoursWithMe != null && (
              <>
                <dt className="text-secondary">Heures avec vous</dt>
                <dd className="text-right font-medium tabular-nums text-heading">{student.hoursWithMe.toLocaleString('fr-FR')} h</dd>
              </>
            )}
          </dl>
        </Card>

        <Button variant="primary" icon={BookOpen} className="h-11 w-full" onClick={go(() => ui.openBilan({ candidateId: student.id, candidateName: name }))}>
          Livret et résumé de la séance
        </Button>

        <ul className="-mx-3 space-y-0.5">
          <ActionRow icon={History} label="Historique des leçons" hint="Leçons passées et leurs résumés" onClick={go(() => ui.openLessons(student.id, name))} />
          <ActionRow icon={ClipboardList} label="Évaluation de départ" onClick={go(() => ui.openEvaluation(student.id))} />
          <ActionRow
            icon={CalendarClock}
            label="Prévision d'examen"
            hint="Indiquer la dizaine où l'élève sera prêt"
            onClick={go(() => ui.openForecast(student.id))}
          />
        </ul>

        {student.removable !== false ? (
          <Button variant="ghost" icon={UserMinus} className="w-full text-danger-dark" onClick={() => setConfirmRemove(true)}>
            Retirer de mes élèves
          </Button>
        ) : (
          <p className="rounded-xl bg-surface-light px-3 py-2 text-center text-[13px] text-secondary">
            Dans votre liste : vous avez fait au moins une heure de conduite avec cet élève.
          </p>
        )}
      </div>
      <ConfirmDialog
        open={confirmRemove}
        title={`Retirer ${name} de vos élèves ?`}
        confirmLabel="Retirer"
        tone="danger"
        pending={remove.isPending}
        error={remove.isError ? (remove.error as Error).message || 'Retrait impossible.' : undefined}
        onConfirm={() => remove.mutate()}
        onCancel={() => setConfirmRemove(false)}
      >
        L’élève n’apparaît plus dans « Mes élèves » ; son dossier, ses leçons et ses résumés sont conservés. Vous pourrez le rajouter
        depuis « Ajouter un élève ».
      </ConfirmDialog>
    </Drawer>
  );
}

function AddStudentSheet({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [q, setQ] = useState('');
  const term = q.trim();

  const { data: results, isFetching } = useQuery({
    queryKey: ['candidate-search', term],
    queryFn: () => instructorApi.searchCandidates(term),
    enabled: term.length >= 1,
  });

  const addMutation = useMutation({
    mutationFn: (c: CandidateSearchResult) => instructorApi.addStudent(c.id),
    onSuccess: (_, c) => {
      void queryClient.invalidateQueries({ queryKey: ['students'] });
      void queryClient.invalidateQueries({ queryKey: ['candidate-search'] });
      toast(`${c.firstName} ${c.lastName} ajouté(e) à votre liste.`);
    },
    onError: (e: Error) => toast(e.message || 'Ajout impossible.'),
  });

  return (
    <Sheet title="Ajouter un élève à votre liste" description="Élèves de l'auto-école, par nom ou e-mail." onClose={onClose} className="sm:max-w-lg">
      <div className="relative">
        <Search size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" aria-hidden />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-autofocus
          aria-label="Rechercher un élève de l'auto-école"
          placeholder="Rechercher un élève de l'auto-école (nom, e-mail)…"
          className="h-11 w-full rounded-xl border border-line bg-surface-light pl-10 pr-10 text-[15px] text-heading outline-none transition-all placeholder:text-muted focus:border-primary-300 focus:bg-white focus:shadow-[0_0_0_4px_#EEF2FF]"
        />
        {isFetching && <Loader2 size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 animate-spin text-muted" aria-hidden />}
      </div>

      {term.length >= 1 && (
        <ul className="mt-3 divide-y divide-slate-100">
          {results && results.length === 0 && !isFetching && <li className="py-3 text-sm text-secondary">Aucun élève trouvé.</li>}
          {(results ?? []).map((c) => {
            const busy = addMutation.isPending && addMutation.variables?.id === c.id;
            return (
              <li key={c.id} className="flex items-center gap-3 py-2.5">
                <StudentAvatar student={c} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-heading">
                    {c.firstName} {c.lastName}
                  </p>
                  <p className="truncate text-xs text-secondary">
                    {c.email ?? 'Sans e-mail'} · {c.vehicleType === 'automatic' ? 'Boîte auto' : 'Boîte manuelle'}
                    {c.assignedToOther ? ' · rattaché à un autre moniteur' : ''}
                  </p>
                </div>
                {c.licenceObtained ? (
                  <Badge tone="ok">
                    <GraduationCap size={12} aria-hidden />
                    Permis obtenu
                  </Badge>
                ) : c.assignedToMe ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-success-dark">
                    <Check size={14} aria-hidden />
                    Dans votre liste
                  </span>
                ) : (
                  <Button size="sm" variant="primary" icon={UserPlus} loading={busy} onClick={() => addMutation.mutate(c)}>
                    {c.assignedToOther ? 'Reprendre' : 'Ajouter'}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Sheet>
  );
}

export default function StudentsView() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['students'],
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
    queryFn: instructorApi.getStudents,
  });
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('tous');
  const [openedId, setOpenedId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const students = useMemo(() => data ?? [], [data]);
  const ready = students.filter((s) => s.readyForExam).length;
  const withExam = students.filter((s) => !!s.examDate).length;
  const shown = useMemo(() => {
    const term = normalize(search.trim());
    return students
      .filter((s) => (filter === 'prets' ? s.readyForExam : filter === 'examen' ? !!s.examDate : true))
      .filter((s) => !term || normalize(`${s.firstName} ${s.lastName}`).includes(term));
  }, [students, search, filter]);
  // Panneau relu dans la liste à jour (rafraîchie toutes les 30 s).
  const opened = students.find((s) => s.id === openedId) ?? null;

  return (
    <section className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-title-md text-heading sm:text-title-lg">Mes élèves</h1>
          <p className="mt-1 text-sm text-secondary">
            {data
              ? `${data.length} élève(s) : ceux qui vous sont confiés et ceux avec qui vous avez fait au moins une heure.`
              : 'Suivi de vos élèves.'}
          </p>
        </div>
        <Button icon={UserPlus} onClick={() => setAdding(true)}>
          Ajouter un élève
        </Button>
      </header>

      {students.length > 0 && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Rechercher parmi mes élèves"
              placeholder="Rechercher parmi mes élèves…"
              className="h-11 w-full rounded-pill border border-line bg-white pl-10 pr-4 text-[15px] text-heading outline-none transition-all placeholder:text-muted focus:border-primary-300 focus:shadow-[0_0_0_4px_#EEF2FF]"
            />
          </div>
          <Segmented
            label="Filtrer mes élèves"
            value={filter}
            onChange={setFilter}
            options={[
              { id: 'tous', label: 'Tous', hint: String(students.length) },
              { id: 'prets', label: 'Prêts', hint: String(ready) },
              { id: 'examen', label: 'Examen prévu', hint: String(withExam) },
            ]}
          />
        </div>
      )}

      {isLoading && <p className="text-secondary">Chargement de vos élèves…</p>}
      {isError && (
        <p role="alert" className="rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
          Impossible de charger vos élèves : {(error as Error).message}
        </p>
      )}

      {data && data.length === 0 && (
        <Card className="p-8 text-center text-secondary">
          <p>Aucun élève pour le moment. Vos élèves, et ceux avec qui vous faites au moins une heure, s’afficheront ici.</p>
          <Button variant="link" className="mt-2" onClick={() => setAdding(true)}>
            Ajouter un élève à votre liste
          </Button>
        </Card>
      )}
      {students.length > 0 && shown.length === 0 && (
        <p className="rounded-row border border-dashed border-line px-4 py-6 text-center text-sm text-secondary">Aucun élève ne correspond.</p>
      )}

      {shown.length > 0 && (
        <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((s) => (
            <StudentCard key={s.id} student={s} onOpen={() => setOpenedId(s.id)} />
          ))}
        </ul>
      )}

      {opened && <StudentPanel student={opened} onClose={() => setOpenedId(null)} />}
      {adding && <AddStudentSheet onClose={() => setAdding(false)} />}
    </section>
  );
}
